import { loadLocalization } from '_shared_/lang/localizations.js';
import setCustomStyles from '_shared_/js/utils/setCustomStyles';
import setTenantAccentColor from '_shared_/js/utils/setTenantAccentColor';
import getBaseUrl from '_shared_/js/utils/getBaseUrl';
import { addOnIdFromLink } from '_shared_/js/utils/addOnLink';
import highlightModel from './highlightModel.js';
import flashcardModel from './flashcardModel.js';
import secondToMMSS from '_shared_/js/utils/secondToMMSS.js';
import * as Sentry from '@sentry/browser';
import { applyTestDataToStore, getTestData } from './TestState/testDataLoader.js';
import { readDevConfig } from './TestState/devConfig.js';
import { HostBridge, contentAPI, isHostCommunicationInitialized, sessionAPI, uiAPI } from './communication';
import { addOnsAPI } from './communication/addOns';

/**
 * Falls back to the raw value when the URL will not parse, since getBaseUrl
 * throws on a malformed one and a single unusable field should not cost every
 * tag.
 *
 * @param {string} issueFullUrl
 * @returns {string} The URL without query parameters, or the input unchanged.
 */
function issueUrlWithoutParams(issueFullUrl) {
    try {
        return getBaseUrl(issueFullUrl);
    } catch {
        return issueFullUrl;
    }
}

class LoadingProcess {
    constructor(eventBus, store) {
        /**
         * This is only used in local environments, where
         * we need to support both loading standalone
         * and inside an iframe from farfalla.
         * For production or staging, we only load the reader
         * from inside an iframe.
         * We might need to tweak this for the new fenice app.
         */
        this.insideIframe = window.self !== window.top;
        this.eventBus = eventBus;
        // We use $store to be within Vue's standards
        this.$store = store;
    }

    init() {
        this.eventBus.$emit('LoadingProcess.init');
        if (this.$store.state.isLoadingProcessFinished) return;
        if (document.readyState === 'complete') {
            this.determineEnvironment();
        } else {
            window.addEventListener(
                'load',
                _e => {
                    this.determineEnvironment();
                },
                false
            );
        }
    }

    determineEnvironment() {
        this.eventBus.$emit('LoadingProcess.determineEnvironment');
        this.determineFileType();

        // E2E tests force standalone mode to bypass Delfino/iframe checks
        if (window.__E2E_STANDALONE_MODE__) {
            this.loadStandAloneDevMode();
            return;
        }

        /**
         * In production or staging, the reader will only work from inside an iframe.
         * This way we avoid showing our dev/debug data to the world.
         */
        if (import.meta.env.VITE_ENV === 'local') {
            this.insideIframe ? this.loadInsideIframe() : this.loadStandAloneDevMode();
        } else {
            this.loadInsideIframe();
        }
    }

    /**
     * Dev helper that picks the reader to boot from a ?file_type= query
     * param. Options are pdf, epub and audio. Any other value boots the
     * unsupported screen, so the knob can exercise that screen by hand.
     */
    determineFileType() {
        this.eventBus.$emit('LoadingProcess.determineFileType');

        const fileType = new URLSearchParams(window.location.search).get('file_type');
        if (!fileType) return;

        this.$store.commit('setFileType', { fileType });
    }

    // ============================================================
    // DELFINO MODE - New Architecture
    // ============================================================

    /**
     * Load via Delfino bridge (new architecture).
     * Uses session.initialize() and content.getContentStructure().
     */
    async loadViaDelfino() {
        this.eventBus.$emit('LoadingProcess.loadViaDelfino');

        const initData = await sessionAPI.initialize();

        // Tag the scope before anything below can report: everything from the
        // language chunk onwards is able to capture, and an untagged event
        // cannot be traced back to the content that caused it.
        this.setSentryTagsFromInitData(initData);

        // Load the tenant's UI language before storing init data (and thus before
        // the UI renders against it). The ~407 synchronous `localizations[lang]`
        // accesses across the app assume the active language is present, so this
        // gate guarantees it. English is bundled as the fallback. Every other
        // language is an on-demand chunk fetched here.
        await this.ensureLanguageLoaded(initData.tenant.lang);

        this.storeSessionInitData(initData);

        void uiAPI.readinessChanged({ isReady: false });
        const content = await contentAPI.getContentStructure();
        this.storeContentStructure(content);

        this.navigateToLastLocation(initData);

        this.offerOnboardingTour();
        this.$store.dispatch('initializeThemeListener');
        this.$store.commit('polpoSessionHasBeenInitiated');
        this.$store.commit('setHighlightModel', highlightModel);
        this.$store.commit('setFlashcardModel', flashcardModel);
        this.$store.dispatch('fetchFlashcards');
        this.$store.dispatch('fetchSelections');
        this.$store.dispatch('fetchBookmarks');
        this.$store.dispatch('fetchAiActions');

        this.loadingProcessFinished();
    }

    /**
     * Ensures the given UI language is available in the store before the UI
     * renders against it. English is bundled eagerly as the fallback, so only
     * other languages trigger an on-demand chunk fetch. On failure (or unknown
     * language) the app stays on the English fallback rather than switching to a
     * language whose translations never loaded, which would break the many
     * unguarded `localizations[lang]` accesses.
     *
     * @param {string} lang - Tenant UI language code (e.g. `es`, `de`).
     * @returns {Promise<void>}
     */
    async ensureLanguageLoaded(lang) {
        // No language given: keep the bundled English default.
        if (!lang) return;

        // English is the bundled fallback, so switch to it without an on-demand fetch.
        if (lang === 'en') {
            this.$store.commit('lang', { lang });
            return;
        }

        let messages;
        try {
            messages = await loadLocalization(lang);
        } catch (error) {
            console.warn(`[LoadingProcess] Failed to load UI language "${lang}", using English fallback:`, error);
            Sentry.captureException(error);
            return;
        }

        // Unknown language with no bundled translations: keep English.
        if (!messages && !this.$store.state.localizations[lang]) return;

        if (messages) {
            this.$store.commit('setLocalization', { lang, messages });
        }
        this.$store.commit('lang', { lang });
    }

    /**
     * Store session initialization data from Delfino.
     *
     * @param {SessionInitData} initData - Data from session.initialize()
     */
    storeSessionInitData(initData) {
        this.$store.commit('parentUrl', { url: initData.tenant.hostLocation });

        // Modes
        this.$store.commit('previewMode', { previewMode: initData.modes.preview });
        this.$store.commit('embeddedMode', { embeddedMode: initData.modes.embedded });
        this.$store.commit('setExhibitionMode', { exhibitionMode: initData.modes.exhibition });

        // User
        this.$store.commit('setUserIsGuest', { userIsGuest: initData.user.isGuest });
        this.$store.commit('anonymousSession', { anonymous_session: initData.user.isAnonymous });

        // Tenant
        this.$store.commit('setTenantId', { tenantId: initData.tenant.id });
        // The UI language is loaded and committed by ensureLanguageLoaded() before
        // this point (see loadViaDelfino), so the active translations are present
        // before any component renders against them.
        if (initData.tenant.customStyles) {
            setCustomStyles(initData.tenant.customStyles);
        }
        setTenantAccentColor(initData.tenant.accentColor);

        // Issue
        this.$store.commit('setIssueId', { issue_id: initData.issue.id });
        this.$store.commit('setFileType', { fileType: initData.issue.fileType });
        this.reportUnsupportedFileType(initData.issue.fileType);
        this.$store.commit('setHasPagebreaks', { hasPagebreaks: initData.issue.hasPagebreaks ?? false });
        this.$store.commit('title', { title: initData.issue.name });
        this.$store.commit('publicationAbout', { publication_about: initData.issue.description });
        this.$store.commit('descriptionRawText', {
            description_raw_text: initData.issue.descriptionRawText,
        });
        this.$store.commit('tags', { tags: initData.issue.tags });
        this.$store.commit('publicationDate', { publication_date: initData.issue.publicationDate });
        this.$store.commit('publicationPlace', { publicationPlace: initData.issue.publicationPlace ?? '' });
        this.$store.commit('issueUrl', { issue_url: getBaseUrl(initData.issue.issueFullUrl) });
        this.$store.commit('setIssueLang', { lang: initData.issue.lang });
        this.$store.commit('shareUrl', { share_url: initData.issue.shareUrl });
        this.$store.commit('setTwitterUser', { twitterUser: initData.issue.twitterUser });
        this.$store.commit('setIsBuyable', { isBuyable: initData.issue.isBuyable });
        this.$store.commit('setIsBuyableButtonText', {
            isBuyableButtonText: initData.issue.buyableButtonText,
        });
        this.$store.commit('setComponentListsIframeUrl', {
            iframeUrl: initData.issue.listsIframeUrl,
        });

        if (initData.issue.issueFullUrl) {
            this.$store.commit('setSharedArticle', { articleId: addOnIdFromLink(initData.issue.issueFullUrl) });
        }

        // Settings
        this.$store.commit('setSettings', initData.settings.reader);
        this.$store.commit('setPromoteAppData', initData.settings.ui.promoteApp);

        const userSettings = initData.settings.user ?? {};
        this.$store.dispatch('setUserSettings', userSettings);
        this.$store.dispatch('initToggleUi', { hiddenOnOpen: initData.settings.ui.hiddenOnOpen ?? false });

        if (userSettings.customColors) {
            this.$store.commit('setCustomColors', userSettings.customColors);
        }

        if (userSettings.sharedColors) {
            this.$store.commit('setSharedColors', userSettings.sharedColors);
        }

        if (userSettings.quickHighlight) {
            this.$store.commit('setQuickHighlightEnabled', userSettings.quickHighlight.enabled);
            this.$store.commit('setQuickHighlightColor', userSettings.quickHighlight.activeColorSlug);
        }

        this.$store.commit('setInitialPanel', initData.initialPanel);

        if (initData.shareLinkSubscription) {
            this.showShareLinkSubscriptionToast(initData.shareLinkSubscription, initData.user?.uuid ?? null);
        }

        // Features
        this.$store.commit('setListeningEnabled', { tts_enabled: initData.features.tts.enabled });
        this.$store.commit('setListeningDriver', { tts_driver: initData.features.tts.driver });
        this.$store.commit('setExtraVoices', { chinese: initData.features.chineseVoice });
        this.$store.commit('setSearchEnabled', { search_enabled: initData.features.search });
        this.$store.commit('setNotesEnabled', { notes_enabled: initData.features.notes });
        this.$store.commit('setBookmarksEnabled', { bookmarks_enabled: initData.features.bookmarks });
        this.$store.commit('setFlashcardsEnabled', { flashcards_enabled: initData.features.flashcards });
        this.$store.commit('setFullscreenEnabled', { fullscreen_enabled: initData.features.fullscreen });
        // Optional during rollout: hosts that don't send it keep the legacy inputs.
        this.$store.commit('setRichTextEditorEnabled', {
            rich_text_editor_enabled: initData.features.richTextEditor ?? false,
        });
        this.$store.commit('setRichTextEmbedHosts', { embed_hosts: initData.features.richTextEmbedHosts });
        this.$store.commit('setAiIntegrationsEnabledOrDisabled', {
            enabled: initData.features.aiIntegrations,
        });
        this.$store.commit('setEnforceConcurrencyLimit', initData.features.concurrencyLimit);
        this.$store.commit('setCopyContentToClipboard', { enabled: initData.features.copyEnabled });
        this.$store.commit('setReaderPlugins', {
            plugins: initData.features.readerPlugins,
            src: initData.features.readerPlugin,
        });
        // The host says who may edit. The dev knob stands in for it where it
        // is readable (local and staging), so editing can be tried without an
        // editor account.
        this.$store.commit('setAddOnsEditing', {
            enabled: initData.features.addOnsEditing === true || readDevConfig().addOnsEditing,
        });
        this.$store.commit('setAddOnBatchSupported', { supported: addOnsAPI.canStoreBatch() });

        const printConfig = initData.features?.print ?? {
            enabled: false,
            printLimitPerJob: 0,
            lifetimeTotalPages: 0,
            lifetimePercentage: 0,
            printedPages: [],
        };

        this.$store.commit('setPrintControlConfig', {
            enabled: Boolean(printConfig.enabled),
            printLimitPerJob: printConfig.printLimitPerJob ?? 0,
            lifetimeTotalPages: printConfig.lifetimeTotalPages ?? 0,
            lifetimePercentage: printConfig.lifetimePercentage ?? 0,
            printedPages: printConfig.printedPages ?? [],
            readerDelivery: Boolean(printConfig.readerDelivery),
        });
        this.$store.commit('setPrintHistory', printConfig.history ?? []);

        // Manifest version
        this.$store.commit('plaPublicationManifestVersion', {
            plaPublicationManifestVersion: initData.manifestVersion ?? '1.0.0',
        });
        this.$store.commit('contentEncryptionIvSeed', {
            tenant_id: initData.tenant.id,
            issue_id: initData.issue.id,
        });

        // Mark as in iframe
        this.$store.commit('inIframe', { inIframe: true });
    }

    /**
     * The tour presents the reader's tools and marks itself as offered the
     * first time it shows, so offering it on the unsupported screen would
     * spend that one offer before the reader opens any content this
     * build can render.
     */
    offerOnboardingTour() {
        if (this.$store.getters.isUnsupportedFormat) return;

        this.$store.dispatch('offerOnboardingTour');
    }

    /**
     * Reports a file type this build has no viewer for. The store already shows
     * an explicit message for it, but a newer manifest reaching an older build
     * is a content anomaly, so it is reported like every other one in this
     * process. Runs after the setFileType commit, which the getter reads.
     *
     * @param {string} fileType - File type as delivered by the host.
     */
    reportUnsupportedFileType(fileType) {
        if (!this.$store.getters.isUnsupportedFormat) return;

        Sentry.captureException(new Error(`Unsupported issue file type "${fileType}"`));
    }

    /**
     * Shows the share-link subscription toast, choosing between the
     * confirmation message and a self-subscription error when the current
     * user is the author of the shared color.
     *
     * @param {{ author: { uuid: string, email: string } }} subscription
     * @param {string | null | undefined} currentUserUuid - UUID of the currently logged-in user; null/undefined for guests
     */
    showShareLinkSubscriptionToast(subscription, currentUserUuid) {
        const SHARE_LINK_TOAST_DURATION_MS = 5000;

        if (currentUserUuid && subscription.author.uuid === currentUserUuid) {
            this.openShareLinkSelfNotAllowedToast(SHARE_LINK_TOAST_DURATION_MS);
            return;
        }

        this.openShareLinkSubscriptionConfirmedToast(subscription, SHARE_LINK_TOAST_DURATION_MS);
    }

    /**
     * Opens the toast confirming the user is now subscribed to a shared color.
     *
     * @param {{ author: { email: string } }} subscription
     * @param {number} durationMs - How long the toast should remain visible
     */
    openShareLinkSubscriptionConfirmedToast(subscription, durationMs) {
        const authorLabel = subscription.author.email;
        const bookTitle = this.$store.state.title;

        const template =
            this.$store.state.localizations?.[this.$store.state.lang]?.sharing?.subscriptionConfirmed ||
            this.$store.state.localizations?.en?.sharing?.subscriptionConfirmed;

        this.$store.commit('openToast', {
            text: template.replace('{author}', authorLabel).replace('{title}', bookTitle),
            durationMs,
        });
    }

    /**
     * Opens the toast warning the user that they cannot subscribe to a color
     * they shared themselves.
     *
     * @param {number} durationMs - How long the toast should remain visible
     */
    openShareLinkSelfNotAllowedToast(durationMs) {
        const text =
            this.$store.state.localizations?.[this.$store.state.lang]?.sharing?.subscriptionSelfNotAllowed ||
            this.$store.state.localizations?.en?.sharing?.subscriptionSelfNotAllowed;

        this.$store.commit('openToast', { text, durationMs });
    }

    /**
     * Store content structure data from Delfino.
     *
     * @param {ContentStructure} content - Data from content.getContentStructure()
     */
    storeContentStructure(content) {
        // Common data
        this.$store.commit('publicationCover', { cover: content.cover });
        // Add-ons are anchored, not paged, so they belong to the content rather
        // than to a format.
        this.$store.commit('setIssueArticles', { articles: content.articles });

        // File-type specific data
        switch (content.fileType) {
            case 'pdf':
                this.storePdfContent(content);
                break;
            case 'epub':
                this.storeEpubContent(content);
                break;
            case 'audio':
                this.storeAudioContent(content);
                break;
        }
    }

    /**
     * Store PDF content structure.
     *
     * @param {PdfContentStructure} content
     */
    storePdfContent(content) {
        this.$store.commit('url', { url: content.url });
        this.$store.commit('filesUrls', { files_urls: content.files_urls });
        this.$store.commit('filesInfo', { files_info: content.files_info });
        this.$store.commit('tableOfContents', { table_of_contents: content.tableOfContents });
    }

    /**
     * Store EPUB content structure.
     *
     * @param {EpubContentStructure} content
     */
    storeEpubContent(content) {
        // Ensure file path has trailing slash
        const filePath = content.filePath.replace(/\/?$/, '/');
        this.$store.commit('filePath', { filePath });
        this.$store.commit('customPreview', { customPreview: content.customPreview });
        this.$store.commit('setFarfallaToc', content.tableOfContents);
        this.$store.commit('setEffectiveToc', content.tableOfContents);
        this.$store.commit('setSpineAndTotalWords', {
            spine: content.spine,
            totalWords: content.totalWords,
        });
        this.$store.commit('setTotalPages', { total: content.totalPages || 0 });

        // A missing printed-page total hides the page indicator, but only a
        // book carrying pagebreak markers can be numbered at all: without
        // them the host has no total to send, and reporting every such book
        // buries the signal under a backlog the reader cannot act on. Markers
        // present and still no total means the numbering itself failed, which
        // is the anomaly worth surfacing.
        if (!(content.totalPages > 0) && this.$store.state.issue.hasPagebreaks) {
            Sentry.captureException(new Error('EPUB content structure missing total_pages'));
        }
    }

    /**
     * Store Audio content structure.
     *
     * @param {AudioContentStructure} content
     */
    storeAudioContent(content) {
        this.$store.commit('loadingAudiobookPlaylist', {
            playlist: content.tracks.map(track => ({
                chapter: track.index + 1,
                title: track.title,
                duration: secondToMMSS(track.duration),
                durationInSeconds: track.duration,
                durationInSecondsPreview: track.durationPreview,
                file: track.fileUrl,
                chunks: track.chunks ?? [],
            })),
            duration: secondToMMSS(content.totalDuration),
            chapters: content.totalChapters,
        });
    }

    /**
     * Navigate to last reading location.
     *
     * @param {SessionInitData} initData
     */
    navigateToLastLocation(initData) {
        const lastLocation = initData.lastLocation;
        if (!lastLocation || initData.modes.preview) return;

        if (this.$store.getters.isPdf) {
            this.$store.dispatch('goToPage', {
                page: lastLocation.page || 1,
            });
        } else if (this.$store.getters.isEpub) {
            // R2 locator format takes priority over legacy R1 format
            if (lastLocation.locator) {
                this.$store.dispatch('setLastLocation', {
                    locator: lastLocation.locator,
                    dontNotify: true,
                });
            } else {
                this.$store.dispatch('setLastLocation', {
                    chapterHref: lastLocation.chapterHref,
                    cfi: lastLocation.cfi,
                    dontNotify: true,
                });
            }
        } else if (this.$store.getters.isAudiobook) {
            this.$store.dispatch('setLastLocation', {
                currentSeek: lastLocation.currentSeek,
                currentTrack: lastLocation.currentTrack,
                dontNotify: true,
            });
        }
    }

    /**
     * Set Sentry tags from init data.
     *
     * @param {SessionInitData} initData
     */
    setSentryTagsFromInitData(initData) {
        try {
            // initData crosses an RPC boundary from a separately deployed host,
            // so its type is a promise about the payload rather than a
            // guarantee. Every access is optional because this literal is
            // evaluated before setTags runs, so one missing branch would
            // otherwise cost the whole set — and a malformed payload is exactly
            // when identifying the event matters most.
            Sentry.setTags({
                host: this.$store?.state?.volpeHost,
                tenant_id: initData?.tenant?.id,
                issue_id: initData?.issue?.id,
                issue_full_url: initData?.issue?.issueFullUrl,
                issue_url: issueUrlWithoutParams(initData?.issue?.issueFullUrl),
                issue_title: initData?.issue?.name,
                issue_lang: initData?.issue?.lang,
                issue_file_type: initData?.issue?.fileType,
                issue_external_id: initData?.issue?.externalId,
                issue_is_buyable: initData?.issue?.isBuyable,
                issue_has_pagebreaks: initData?.issue?.hasPagebreaks,
                manifest_version: initData?.manifestVersion,
                preview_mode: initData?.modes?.preview,
                preview_percentage: initData?.settings?.reader?.preview?.percentage,
                user_is_guest: initData?.user?.isGuest,
                user_is_anonymous: initData?.user?.isAnonymous,
            });
        } catch (error) {
            console.error('Error setting sentry tags:', error);
        }
    }

    // ============================================================
    // IFRAME LOADING (tries Delfino first, falls back to legacy)
    // ============================================================

    // This is the loading process when Volpe is inside a Farfalla instance
    async loadInsideIframe() {
        this.eventBus.$emit('LoadingProcess.loadInsideIframe');

        if (isHostCommunicationInitialized()) {
            try {
                await this.loadViaDelfino();
                return;
            } catch (error) {
                console.warn('[LoadingProcess] Delfino loading failed:', error);
                Sentry.captureException(error);
                return;
            }
        }

        // Delfino not initialized - expected in local dev in standalone mode, but a problem in production
        if (import.meta.env.VITE_ENV === 'local' && HostBridge.isStandalone()) {
            await this.loadStandAloneDevMode();
            return;
        }

        // Production/staging without Delfino initialized - report to Sentry
        console.warn('[LoadingProcess] Delfino not initialized in production iframe');
        Sentry.captureMessage('Delfino not initialized in production iframe', 'error');
    }

    // This is the loading process when running Volpe outside a Farfalla instance
    async loadStandAloneDevMode() {
        this.eventBus.$emit('LoadingProcess.loadStandAloneDevMode');

        // Single source of truth for every dev knob (URL param wins, then
        // localStorage for live knobs that persist, then the per-option default).
        const devConfig = readDevConfig();

        this.$store.commit('inIframe', {
            inIframe: false,
        });
        // PDF layout default comes from the dev config (defaults to vertical scroll).
        this.$store.commit('setSettings', {
            layout: devConfig.pdfLayout,
        });

        this.offerOnboardingTour();

        await this.initMockPolpoSession();

        const testData = getTestData();

        // Apply all test data to store using helper function
        applyTestDataToStore(this.$store, testData, devConfig);

        // The lists are fetched once the test data has landed: each fetch reads its
        // availability gate up front, which the test data supplies (StandAloneAllFeatures,
        // devConfig.anonymous). Dispatching before it would leave bookmarks and flashcards
        // unfetched for the whole standalone session.
        this.fetchStandaloneLists();

        // In production the host delivers the spine via Delfino
        // (storeEpubContent). Standalone mode has no host, so features
        // gated on it (the range paginator and read progress) never
        // render. Derive it from the RWPM manifest the navigator loads.
        await this.seedStandaloneEpubSpine();

        this.$store.dispatch('initToggleUi');

        // Theme default comes from the dev config (defaults to dark). A ?theme=
        // param or persisted choice wins via the config's precedence rules.
        this.$store.dispatch('setTheme', { theme: devConfig.theme });

        this.$store.dispatch('initializeThemeListener');

        if (!this.isPdf && localStorage.getItem('notifyLocationChange')) {
            this.$store.dispatch('setLastLocation', {
                ...JSON.parse(localStorage.getItem('notifyLocationChange')),
                dontNotify: true,
            });
        }

        this.loadingProcessFinished();
    }

    /**
     * Standalone dev only: builds the EPUB spine from the exploded book's
     * RWPM manifest (`readingOrder`), the same file the R2 navigator loads.
     * Production gets the spine from the host via storeEpubContent.
     */
    async seedStandaloneEpubSpine() {
        if (this.isPdf || this.$store.state.fileType !== 'epub') return;
        if (this.$store.state.epub.spine?.length) return;

        // The `?book=` param overrides the publication at the R2 level
        // (see Readium/EpubViewer.js) without touching the store's filePath,
        // so it must win here too or the spine would come from another book.
        const bookOverride = new URLSearchParams(window.location.search).get('book');
        const filePath = (bookOverride || this.$store.state.epub.filePath || '').replace(/\/?$/, '/');
        if (!filePath) return;

        try {
            const response = await fetch(`${window.location.origin}/${filePath}manifest.json`);
            // Vite's SPA fallback answers 200 text/html for missing files.
            if (!response.ok || !response.headers.get('content-type')?.includes('json')) return;
            const manifest = await response.json();
            const spine = (manifest.readingOrder || []).map(item => ({ href: item.href }));
            if (!spine.length) return;

            this.$store.commit('setSpineAndTotalWords', { spine, totalWords: 0 });
        } catch (_error) {
            // No readable manifest → no spine: the reader still works, just
            // without the range paginator, exactly as before.
        }
    }

    async initMockPolpoSession() {
        this.eventBus.$emit('LoadingProcess.initMockPolpoSession');

        this.$store.commit('polpoSessionHasBeenInitiated');
        this.$store.commit('setHighlightModel', highlightModel);
        this.$store.commit('setFlashcardModel', flashcardModel);

        this.$store.commit('setCopyContentToClipboard', { enabled: true });
        this.$store.commit('aiActions', ['explain-test', 'expand-test', 'summarize-test']);

        if (!this.$store.state.plaPublicationManifestVersion) {
            this.$store.commit('plaPublicationManifestVersion', {
                plaPublicationManifestVersion: '1.0.0',
            });
        }
    }

    /**
     * Mirrors the list fetches loadViaDelfino dispatches once the session is initiated, so
     * the standalone reader exercises highlights, bookmarks and flashcards alike. Under the
     * E2E mock the lists answer empty. Without a host they fail and settle empty.
     */
    fetchStandaloneLists() {
        this.$store.dispatch('fetchFlashcards');
        this.$store.dispatch('fetchSelections');
        this.$store.dispatch('fetchBookmarks');
    }

    loadingProcessFinished() {
        if (this.$store.state.isLoadingProcessFinished) return;
        this.$store.commit('loadingProcessIsFinished');

        document.querySelector('html').classList.add('ready');
    }
}

export default LoadingProcess;
